// src/store/modules/competition.js
import { defineStore } from 'pinia'
import { fetchCompetitionData } from '@/services/api'
import config from '@/config'

// Helper function to check if competition is in the future
function isFutureCompetition(competition) {
    if (!competition || !competition.Date) return false

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const compDate = new Date(competition.Date)
    compDate.setHours(0, 0, 0, 0)

    return compDate > today
}

function hasEndedCompetition(competition) {
    if (!competition || !competition.Date) return false

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const compDate = new Date(competition.Date)
    compDate.setHours(0, 0, 0, 0)

    return compDate < today
}

export const useCompetitionStore = defineStore('competition', {
    state: () => ({
        competition: null,
        results: [],
        tracks: [],
        loading: false,
        error: null,
        lastUpdated: null,
        refreshInterval: null,
        timeUntilNextUpdate: 60,
        updateInProgress: false, // Track if an update is already in progress
        autoUpdateEnabled: true,  // Allow toggling auto-updates

        // Properties for handling subcompetitions
        subCompetitions: [], // Array of subcompetitions
        activeCompetitionId: null, // ID of the currently active (sub)competition
        activeCompetitionIndex: -1, // Index of the active competition (-1 for main/overall)

        // Overall results (combined scores from all rounds)
        overallResults: [],
    }),

    getters: {
        groupedByDivision(state) {
            // Use overall results when viewing the main competition
            const resultsToGroup = state.activeCompetitionIndex === -1 && state.overallResults.length > 0
                ? state.overallResults
                : state.results;

            if (!resultsToGroup || !resultsToGroup.length) return {}

            return resultsToGroup.reduce((divisions, player) => {
                const division = player.ClassName || 'asd'

                if (!divisions[division]) {
                    divisions[division] = []
                }

                divisions[division].push(player)
                return divisions
            }, {})
        },

        competitionStatus(state) {
            const activeComp = this.activeCompetition;
            if (!activeComp) return 'unknown'

            const hasStarted = state.results.some(player => player.hasStarted)

            // Special handling for the overall view in multi-round competitions
            if (state.activeCompetitionIndex === -1 && state.subCompetitions && state.subCompetitions.length > 0) {
                // Check if any rounds are still active or upcoming
                const hasActiveOrUpcomingRounds = state.subCompetitions.some(round =>
                    !hasEndedCompetition(round) || isFutureCompetition(round)
                );

                // Check if any rounds have started
                const hasAnyRoundStarted = state.subCompetitions.some(round =>
                        round.Results && round.Results.some(player =>
                            player.PlayerResults && player.PlayerResults.some(hole => hole && hole.Result)
                        )
                );

                if (hasActiveOrUpcomingRounds) {
                    // At least one round is still active or upcoming
                    if (hasAnyRoundStarted) {
                        return 'in_progress'; // Competition is in progress
                    } else {
                        return 'not_started'; // No rounds have started yet
                    }
                } else {
                    // All rounds have ended
                    return 'ended';
                }
            }

            // For individual rounds, use the normal logic
            if (isFutureCompetition(activeComp)) {
                return 'upcoming'
            } else if(hasEndedCompetition(activeComp)){
                return 'ended'
            } else if (!hasStarted) {
                return 'not_started'
            } else {
                return 'in_progress'
            }
        },

        hasEndedCompetition() {
            // Use activeCompetition instead of state.competition
            return hasEndedCompetition(this.activeCompetition)
        },

        isFutureCompetition() {
            // Use activeCompetition instead of state.competition
            return isFutureCompetition(this.activeCompetition)
        },

        timeUntilStart() {
            // Use activeCompetition instead of state.competition
            const activeComp = this.activeCompetition;
            if (!activeComp || !activeComp.Date || !activeComp.Time) {
                return null
            }

            const now = new Date()
            const [year, month, day] = activeComp.Date.split('-')
            const [hours, minutes] = activeComp.Time.split(':')

            const startTime = new Date(year, month - 1, day, hours, minutes)

            if (startTime <= now) return null

            const diffMs = startTime - now
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
            const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
            const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

            if (diffDays > 0) {
                return `${diffDays} day${diffDays > 1 ? 's' : ''} ${diffHours} hour${diffHours > 1 ? 's' : ''}`
            } else if (diffHours > 0) {
                return `${diffHours} hour${diffHours > 1 ? 's' : ''} ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`
            } else {
                return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`
            }
        },

        divisionCounts(state) {
            // Use overall results when viewing the main competition
            const resultsToCount = state.activeCompetitionIndex === -1 && state.overallResults.length > 0
                ? state.overallResults
                : state.results;

            if (!resultsToCount || !resultsToCount.length) return {}

            return resultsToCount.reduce((counts, player) => {
                const division = player.ClassName || 'Unspecified'
                counts[division] = (counts[division] || 0) + 1
                return counts
            }, {})
        },

        // Get all available competitions (main + subcompetitions)
        allCompetitions(state) {
            if (!state.competition) return []

            const competitions = [
                {
                    id: state.competition.ID.toString(),
                    name: 'Overall',
                    type: state.competition.Type,
                    date: state.competition.Date,
                    index: -1 // Index -1 represents the main/overall competition
                }
            ]

            // Add subcompetitions if they exist
            if (state.subCompetitions && state.subCompetitions.length > 0) {
                state.subCompetitions.forEach((comp, index) => {
                    competitions.push({
                        id: comp.ID.toString(),
                        name: comp.Name,
                        type: comp.Type,
                        date: comp.Date,
                        index
                    })
                })
            }

            return competitions
        },

        // Get the active competition object
        activeCompetition(state) {
            if (state.activeCompetitionIndex === -1) {
                return state.competition
            } else if (state.subCompetitions && state.subCompetitions.length > state.activeCompetitionIndex) {
                return state.subCompetitions[state.activeCompetitionIndex]
            }
            return null
        }
    },

    actions: {
        async fetchData(id = config.api.competitionId, showLoading = true) {
            // Prevent concurrent updates
            if (this.updateInProgress) return;

            this.updateInProgress = true;

            // Only show loading indicator on initial load or manual refresh
            if (showLoading) {
                this.loading = true;
            }

            this.error = null;

            try {
                const data = await fetchCompetitionData(id);

                if (data.Competition) {
                    this.competition = data.Competition;

                    // Check for subcompetitions (rounds)
                    if (data.Competition.SubCompetitions && data.Competition.SubCompetitions.length > 0) {
                        this.subCompetitions = data.Competition.SubCompetitions;

                        // Set active competition to first subcompetition by default if not already set
                        if (!this.activeCompetitionId) {
                            this.setActiveCompetition(this.subCompetitions[0].ID, 0);
                        }
                    } else {
                        // If no subcompetitions, set the main competition as active
                        this.setActiveCompetition(this.competition.ID, -1);
                        this.subCompetitions = [];
                    }

                    // Process results for the active competition
                    this.processResults();

                    // Calculate overall results if we have subcompetitions
                    if (this.subCompetitions && this.subCompetitions.length > 0) {
                        this.calculateOverallResults();
                    }
                }

                this.lastUpdated = new Date();
                this.timeUntilNextUpdate = 60;
            } catch (error) {
                console.error('Error fetching data:', error);
                this.error = error.message || 'Failed to load data';
            } finally {
                this.loading = false;
                this.updateInProgress = false;
            }
        },

        // Process results for the currently active competition
        processResults() {
            const activeComp = this.activeCompetition;

            if (!activeComp) {
                this.results = [];
                this.tracks = [];
                return;
            }

            // Process results for the active competition
            if (activeComp.Results && activeComp.Results.length > 0) {
                const processedResults = activeComp.Results.map(result => ({
                    ...result,
                    Place: parseInt(result.Place) || 0,
                    Sum: parseInt(result.Sum) || 0,
                    Diff: parseInt(result.Diff) || 0,
                    hasStarted: Array.isArray(result.PlayerResults) &&
                        result.PlayerResults.some(hole => hole && hole.Result)
                }));
                this.results = processedResults;
            } else {
                this.results = [];
            }

            // Process tracks for the active competition
            if (activeComp.Tracks && activeComp.Tracks.length > 0) {
                this.tracks = activeComp.Tracks;
            } else {
                this.tracks = [];
            }
        },

        // Calculate overall results by combining scores from all rounds
        // Modified calculateOverallResults method to include hole-by-hole data

// Calculate overall results by combining scores from all rounds
        calculateOverallResults() {
            // Initialize playerMap to collect data across rounds
            const playerMap = new Map();

            // Process all rounds (subcompetitions)
            this.subCompetitions.forEach((round, roundIndex) => {
                if (round?.Results && Array.isArray(round.Results)) {
                    round.Results.forEach(player => {
                        const userId = player.UserID;

                        // Skip if player has no UserID
                        if (!userId) return;

                        // If player not in map yet, add them
                        if (!playerMap.has(userId)) {
                            playerMap.set(userId, {
                                UserID: userId,
                                Name: player.Name,
                                ClassName: player.ClassName,
                                CountryCode: player.CountryCode,
                                Group: player.Group,
                                roundScores: [],
                                totalSum: 0,
                                totalDiff: 0,
                                hasStarted: false,
                                roundCount: 0
                            });
                        }

                        const playerData = playerMap.get(userId);

                        // Check if player has started this round
                        const hasStartedRound = Array.isArray(player.PlayerResults) &&
                            player.PlayerResults.some(hole => hole && hole.Result);

                        if (hasStartedRound) {
                            playerData.hasStarted = true;
                            playerData.roundCount++;

                            // Add round scores WITH hole-by-hole data
                            playerData.roundScores[roundIndex] = {
                                Sum: parseInt(player.Sum) || 0,
                                Diff: parseInt(player.Diff) || 0,
                                Place: parseInt(player.Place) || 0,
                                // Store the PlayerResults array for each round
                                holeData: player.PlayerResults
                            };

                            // Update totals
                            playerData.totalSum += parseInt(player.Sum) || 0;
                            playerData.totalDiff += parseInt(player.Diff) || 0;
                        }
                    });
                }
            });

            // Convert Map to Array and calculate overall standings
            let overallResults = Array.from(playerMap.values()).map(player => ({
                UserID: player.UserID,
                ScorecardID: player.UserID, // Use UserID as ScorecardID for overall view
                Name: player.Name,
                ClassName: player.ClassName,
                CountryCode: player.CountryCode,
                Group: player.Group,
                Sum: player.totalSum,
                Diff: player.totalDiff,
                hasStarted: player.hasStarted,
                roundScores: player.roundScores,
                roundCount: player.roundCount
            }));

            // Sort by total score (totalSum)
            overallResults = overallResults
                .filter(player => player.hasStarted)
                .sort((a, b) => {
                    // First sort by whether they've started all rounds
                    if (a.roundCount !== b.roundCount) {
                        return b.roundCount - a.roundCount;
                    }

                    // Then sort by score
                    return a.Sum - b.Sum;
                });

            // Assign overall places
            overallResults.forEach((player, index) => {
                player.Place = index + 1;

                // Handle ties - if players have the same sum, they get the same place
                if (index > 0 && player.Sum === overallResults[index - 1].Sum) {
                    player.Place = overallResults[index - 1].Place;
                }
            });

            this.overallResults = overallResults;
        },

        // Set the active competition
        setActiveCompetition(id, index) {
            this.activeCompetitionId = id?.toString();
            this.activeCompetitionIndex = index;

            // Update results and tracks for the newly selected competition
            this.processResults();
        },

        setupAutoRefresh() {
            // Clear any existing intervals first
            this.clearIntervals();

            // Set up auto-refresh using interval from config
            this.refreshInterval = setInterval(() => {
                if (this.autoUpdateEnabled) {
                    // Pass false to avoid showing loading indicator on auto-updates
                    this.fetchData(undefined, false);
                }
            }, config.refreshIntervals.standard);
        },

        // Toggle auto-update functionality
        toggleAutoUpdate() {
            this.autoUpdateEnabled = !this.autoUpdateEnabled;

            // If toggled on and there was no interval, set it up
            if (this.autoUpdateEnabled && !this.refreshInterval) {
                this.setupAutoRefresh();
            }

            return this.autoUpdateEnabled;
        },

        decrementCountdown() {
            this.timeUntilNextUpdate--;
        },

        resetCountdown() {
            this.timeUntilNextUpdate = 60;
        },

        clearIntervals() {
            if (this.refreshInterval) {
                clearInterval(this.refreshInterval);
                this.refreshInterval = null;
            }
        },

        // Manual refresh - shows loading indicator
        manualRefresh(id = config.api.competitionId) {
            return this.fetchData(id, true);
        }
    }
})
